"use client";

/**
 * SubmitDomainForm — nominate a domain for the Web Index.
 *
 * Posts to the proxied public endpoint (see next.config.ts rewrite). The
 * backend does the real validation, normalisation, rate limiting and
 * junk-filtering; the checks here exist only to give instant feedback on
 * obvious mistakes, never as the security boundary.
 *
 * `preview()` deliberately MIRRORS the server's _normalise_domain so the user
 * sees what will actually be submitted before they press the button. It is a
 * display aid only — if the two ever drift, the server wins and the outcome
 * panel shows the domain the server settled on.
 *
 * Every outcome the API can return is handled explicitly — a submission that
 * is already indexed, already queued or unsuitable must say so plainly rather
 * than pretending it was accepted.
 */

import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Check,
  Clock,
  Globe,
  Loader2,
  Search,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";

const ENDPOINT = "/api/index/submit-domain";
const DISCOVERY = "/api/index/discovery";
const SUMMARY = "/api/index/domain-summary";
const STATUS_URL = "https://askfinz.ai/indexing_in_progress";
const WEB_SAFETY_URL = "https://askfinz.com/web-safety";

const EXAMPLES = ["docs.python.org", "arxiv.org", "who.int"];

type Outcome = {
  status: "accepted" | "queued" | "already_queued" | "already_indexing" | "not_suitable";
  domain: string;
  message: string;
};

type ReindexDelta = {
  at: string | null;
  added: number;
  changed: number;
  unchanged: number;
  purged: number;
};

type DomainSummary = {
  domain: string;
  indexed: boolean;
  page_count: number;
  last_indexed: string | null;
  last_reindex: ReindexDelta | null;
  in_progress: { added: number; changed: number; unchanged: number; purged: number } | null;
};

/** Compact relative time, e.g. "3 d ago". Display only.
 *
 * A MISSING TIMESTAMP IS "never", NOT "just now". This returned "just now" for null, so a
 * domain that had never been read once displayed "last re-indexed just now" next to "0 pages
 * indexed" — the most recent-looking answer possible for the emptiest possible state.
 * `last_indexed` is also NOT NULL server-side and an index reset writes 'epoch', so guard that
 * too or it renders "57 y ago" (the report's own relTime already does both).
 */
function relTime(iso: string | null): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "recently";
  if (t <= 86400000) return "never";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 90) return "just now";
  const m = s / 60;
  if (m < 60) return `${Math.round(m)} min ago`;
  const h = m / 60;
  if (h < 24) return `${Math.round(h)} h ago`;
  const d = h / 24;
  if (d < 30) return `${Math.round(d)} d ago`;
  const mo = d / 30;
  if (mo < 12) return `${Math.round(mo)} mo ago`;
  return `${Math.round(mo / 12)} y ago`;
}

const OUTCOME_STYLE: Record<
  Outcome["status"],
  { icon: typeof Check; tone: "good" | "info" | "warn"; label: string }
> = {
  accepted:         { icon: Check,         tone: "good", label: "Accepted" },
  queued:           { icon: Clock,         tone: "good", label: "Queued" },
  already_queued:   { icon: Clock,         tone: "info", label: "Already queued" },
  already_indexing: { icon: Search,        tone: "info", label: "Already indexed" },
  not_suitable:     { icon: TriangleAlert, tone: "warn", label: "Not a fit" },
};

const TONE_CLASS = {
  good: "border-emerald-500/30 bg-emerald-500/10",
  info: "border-foreground/20 bg-foreground/5",
  warn: "border-amber-500/30 bg-amber-500/10",
};

const TONE_TEXT = {
  good: "text-emerald-700 dark:text-emerald-400",
  info: "text-foreground/80",
  warn: "text-amber-700 dark:text-amber-400",
};

/** Mirrors the server's _normalise_domain, for the live preview only. */
function preview(raw: string): string {
  let d = (raw || "").trim().toLowerCase();
  if (!d) return "";
  if (d.includes("//")) d = d.split("//")[1] ?? "";
  d = d.split("/")[0].split("?")[0].split("#")[0];
  d = d.split("@").pop() ?? "";
  d = d.split(":")[0];
  if (d.startsWith("www.")) d = d.slice(4);
  return d.replace(/^\.+|\.+$/g, "");
}

export function SubmitDomainForm() {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [summary, setSummary] = useState<DomainSummary | null>(null);
  const [queued, setQueued] = useState<number | null>(null);

  // Live credibility figure — how much is already in the reading list.
  useEffect(() => {
    let on = true;
    fetch(DISCOVERY, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (on && d && typeof d.seed_count === "number") setQueued(d.seed_count);
      })
      .catch(() => {});
    return () => {
      on = false;
    };
  }, []);

  const shown = preview(domain);
  const differs = shown && shown !== domain.trim().toLowerCase();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = domain.trim();
    if (!value) {
      setError("Enter a domain first.");
      return;
    }
    setBusy(true);
    setError("");
    setOutcome(null);
    setSummary(null);
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: value }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || "That didn't go through. Try again shortly.");
        return;
      }
      const oc = data as Outcome;
      setOutcome(oc);
      setDomain("");
      // For an already-indexed domain, pull the live index snapshot so we can show
      // when it was last re-indexed and how the corpus changed (new/changed/etc.).
      if (oc.status === "already_indexing" && oc.domain) {
        fetch(`${SUMMARY}/${encodeURIComponent(oc.domain)}`, { cache: "no-store" })
          .then((r) => (r.ok ? r.json() : null))
          .then((s) => {
            if (s && !s.error) setSummary(s as DomainSummary);
          })
          .catch(() => {});
      }
    } catch {
      setError("Couldn't reach the index just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const style = outcome ? OUTCOME_STYLE[outcome.status] : null;
  const Icon = style?.icon;

  return (
    <div className="relative rounded-2xl border hairline-strong bg-card overflow-hidden">
      <div aria-hidden className="absolute inset-0 grid-bg-dense opacity-[0.10]" />

      <div className="relative p-6 md:p-8">
        <div className="flex items-center justify-between gap-4">
          <div className="kicker">Add to the reading list</div>
          {queued != null && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-foreground/50">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {queued.toLocaleString()} sites already in
            </span>
          )}
        </div>

        <form onSubmit={submit} className="mt-5">
          <label htmlFor="domain" className="sr-only">
            Domain
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Globe
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/35"
                aria-hidden
              />
              <input
                id="domain"
                name="domain"
                type="text"
                inputMode="url"
                autoComplete="url"
                spellCheck={false}
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                disabled={busy}
                className="w-full rounded-xl border hairline-strong bg-background pl-11 pr-4 py-3.5 font-mono text-[15px] outline-none transition-colors focus:border-[hsl(var(--accent)/0.5)] disabled:opacity-60"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !shown}
              className="group shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-foreground text-background px-6 py-3.5 text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Checking…" : "Submit"}
              {!busy && (
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              )}
            </button>
          </div>

          {/* Live normalisation preview — shows exactly what will be sent. */}
          <div className="mt-3 min-h-[20px]">
            {differs ? (
              <p className="text-[13px] text-foreground/60 font-mono">
                will be submitted as{" "}
                <span className="text-foreground/90">{shown}</span>
              </p>
            ) : (
              <p className="text-[13px] text-foreground/55">
                A full URL is fine &mdash; we&#39;ll trim it to the domain. No account needed.
              </p>
            )}
          </div>

          {/* Web-safety disclaimer — final acceptance depends on the pipeline's
              safety check, so set the expectation up front. */}
          <p className="mt-2 flex items-start gap-1.5 text-[12px] leading-relaxed text-foreground/50">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground/40" aria-hidden />
            <span>
              Only domains that meet our{" "}
              <a
                href={WEB_SAFETY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-foreground/80"
              >
                web-safety policy
              </a>{" "}
              are accepted &mdash; every submission is checked before, during and after indexing.
            </span>
          </p>
        </form>

        {!outcome && !error && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.16em] text-foreground/40">
              Try
            </span>
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => setDomain(ex)}
                className="rounded-full border hairline px-3 py-1 font-mono text-[12px] text-foreground/65 hover:text-foreground hover:bg-foreground/5 transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-foreground/80">{error}</p>
          </div>
        )}

        {outcome && style && Icon && (
          <div className={`mt-5 rounded-xl border px-4 py-4 ${TONE_CLASS[style.tone]}`}>
            <div className="flex items-start gap-3">
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${TONE_TEXT[style.tone]}`} />
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${TONE_TEXT[style.tone]}`}>
                  {style.label} &mdash;{" "}
                  <span className="font-mono font-normal break-all">{outcome.domain}</span>
                </p>
                <p className="mt-1 text-[13px] text-foreground/70 leading-relaxed">
                  {outcome.message}
                </p>
                {/* Already indexed → show the last re-index snapshot + a link to
                    the full gated report. */}
                {/* Only when there is something to report. A summary can come back with
                    page_count 0 (registered, never read), and rendering the block anyway
                    produced the "0 pages indexed" card this guard exists to prevent. */}
                {outcome.status === "already_indexing" && summary && summary.page_count > 0 && (
                  <div className="mt-3 rounded-lg border hairline bg-background/50 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                      <span className="text-[12px] text-foreground/60">
                        <span className="font-semibold text-foreground/85">
                          {summary.page_count.toLocaleString()}
                        </span>{" "}
                        pages indexed
                      </span>
                      <span className="text-[12px] text-foreground/55">
                        last re-indexed {relTime(summary.last_reindex?.at ?? summary.last_indexed)}
                      </span>
                    </div>
                    {summary.last_reindex && (
                      <div className="mt-2.5 grid grid-cols-4 gap-2 text-center">
                        {([
                          ["new", summary.last_reindex.added, "text-emerald-600 dark:text-emerald-400"],
                          ["changed", summary.last_reindex.changed, "text-foreground/80"],
                          ["unchanged", summary.last_reindex.unchanged, "text-foreground/60"],
                          ["purged", summary.last_reindex.purged, "text-amber-600 dark:text-amber-400"],
                        ] as const).map(([label, n, cls]) => (
                          <div key={label} className="rounded-md border hairline bg-card/60 py-1.5">
                            <div className={`font-mono text-[15px] font-semibold ${cls}`}>
                              {n.toLocaleString()}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.1em] text-foreground/45">
                              {label}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {summary.in_progress && (
                      <p className="mt-2 text-[11px] text-foreground/45">
                        Re-indexing in progress &mdash; counts update as it runs.
                      </p>
                    )}
                  </div>
                )}

                {(outcome.status === "accepted" ||
                  outcome.status === "queued" ||
                  outcome.status === "already_queued") && (
                  <a
                    href={STATUS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium underline underline-offset-2 hover:opacity-80"
                  >
                    Watch the queue
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
                {/* The report is only worth opening once pages exist — otherwise this
                    button leads to an empty page. Until the summary arrives, trust the
                    server's already_indexing verdict, which now means "has pages". */}
                {outcome.status === "already_indexing" && (!summary || summary.page_count > 0) && (
                  <a
                    href={`${STATUS_URL}/${encodeURIComponent(outcome.domain)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3.5 py-2 text-[13px] font-medium text-background hover:opacity-90"
                  >
                    <BarChart3 className="h-3.5 w-3.5" />
                    View detailed report
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setOutcome(null);
                    setSummary(null);
                  }}
                  className="mt-3 ml-4 inline-flex text-[13px] text-foreground/55 hover:text-foreground underline underline-offset-2"
                >
                  Submit another
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
